import apiClient from '@/services/api';

/**
 * Build query parameters from a parameter object
 */
export const buildQueryParams = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        // Handle array parameters (like brands)
        value.forEach(item => searchParams.append(key, item));
      } else {
        searchParams.append(key, value.toString());
      }
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : '';
};

/**
 * Validate required parameters
 */
export const validateRequiredParams = (
  params: Record<string, any>,
  requiredKeys: string[]
): void => {
  const missingKeys = requiredKeys.filter(key => !(key in params) || params[key] === undefined);
  if (missingKeys.length > 0) {
    throw new Error(`Missing required parameters: ${missingKeys.join(', ')}`);
  }
};

/**
 * Simple GET request utility
 */
export const apiGet = async <T>(endpoint: string, params?: Record<string, any>): Promise<T> => {
  const queryString = params ? buildQueryParams(params) : '';
  const response = await apiClient.get<T>(`${endpoint}${queryString}`);
  return response.data;
};

/**
 * Simple POST request utility
 */
export const apiPost = async <T>(
  endpoint: string,
  data: Record<string, unknown>,
  params?: Record<string, any>
): Promise<T> => {
  const queryString = params ? buildQueryParams(params) : '';
  const response = await apiClient.post<T>(`${endpoint}${queryString}`, data);
  return response.data;
};

/**
 * Simple PUT request utility
 */
export const apiPut = async <T>(
  endpoint: string,
  data: Record<string, unknown>,
  params?: Record<string, any>
): Promise<T> => {
  const queryString = params ? buildQueryParams(params) : '';
  const response = await apiClient.put<T>(`${endpoint}${queryString}`, data);
  return response.data;
};

/**
 * Simple DELETE request utility
 */
export const apiDelete = async <T>(endpoint: string, params?: Record<string, any>): Promise<T> => {
  const queryString = params ? buildQueryParams(params) : '';
  const response = await apiClient.delete<T>(`${endpoint}${queryString}`);
  return response.data;
};
